import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ActionIcon, Box, Flex, Input, Text } from '@mantine/core';
import Table from '../../../../components/Table';
import {
  TbArrowRight,
  TbLayoutSidebarLeftExpand,
  TbLayoutSidebarRightExpand,
  TbPoint,
  TbSearch
} from 'react-icons/tb';
import { idCreator } from '../../../../pages/Project/utils/idCreator';

export const Errors = ({
  data,
  onSelectError,
  onSelectWarning,
  onSelectMandatoryIssue,
  selectedCategory,
  isOpenSidebar,
  toggleSidebar,
  hasPanelBtn = false,
  issue
}) => {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');

  const { projectId, periodId } = useParams();

  const onNavigate = (fileId, sheetId) => {
    navigate(
      `/data-extraction/${projectId}/${periodId}?fileId=${fileId}&sheetId=${sheetId}&tab=transformation`
    );
  };

  const _category = useMemo(
    () => data?.find(category => category.category === selectedCategory?.name),
    [data, selectedCategory]
  );

  const _errors = useMemo(() => {
    if (_category)
      return _category.tables
        .map(item => item.errors?.map(val => ({ ...val, tableName: item.name })))
        .flat();
    return [];
  }, [_category]);

  const _warnings = useMemo(() => {
    if (_category)
      return _category.tables
        .map(item => item.warnings?.map(val => ({ ...val, tableName: item.name })))
        .flat();

    return [];
  }, [_category]);

  const _mandatoryIssues = useMemo(() => {
    if (_category)
      return _category.tables
        .map(item => item.mandatory_fixes?.map(val => ({ ...val, tableName: item.name })))
        .flat();
    return [];
  }, [_category]);

  const handleIssueSelection = value => {
    const type = value?.dataType;
    if (value?.type === 'TransformationError') {
      onNavigate(value.file_id, value.sheet_id);
    } else if (type === 'warning') {
      onSelectWarning({
        category: selectedCategory.name,
        table: value.tableName,
        field: value.field,
        warning: value,
        type
      });
    } else if (type === 'mandatory') {
      onSelectMandatoryIssue({
        category: selectedCategory.name,
        table: value.tableName,
        error: value,
        field: value.field,
        type
      });
    } else {
      onSelectError({
        category: selectedCategory.name,
        table: value.tableName,
        error: value,
        field: value.field,
        type
      });
    }
  };

  const columns = [
    {
      title: 'Issue Name',
      key: 'type',
      render: value => {
        const type = value?.dataType;

        const color =
          type === 'error'
            ? 'var(--mantine-color-red-6)'
            : type === 'mandatory'
              ? 'var(--mantine-color-orange-6)'
              : 'var(--mantine-color-gray-6)';

        return (
          <Flex
            id={`output-table-error-row-${idCreator(type)}-${idCreator(value.tableName)}-${idCreator(value.field)}`}
            sx={() => ({ cursor: 'pointer' })}
            align="flex-start"
            gap="4px"
            onClick={() => handleIssueSelection(value)}
          >
            <TbPoint
              style={{
                minWidth: '16px'
              }}
              fontSize="16px"
              color={color}
            />

            <Text
              id={`output-table-error-label-${idCreator(type)}-${idCreator(value.tableName)}-${idCreator(value.field)}`}
              td="underline"
              c={color}
              fz="12px"
              mt="-2px"
            >
              {value?.type === 'TransformationError'
                ? 'Transformation error'
                : value?.field || value?.tableName}
              &nbsp;{value.count > 0 && `(${value.count})`}
            </Text>

            <TbArrowRight
              fontSize="16px"
              style={{
                minWidth: '16px'
              }}
              color={color}
            />
          </Flex>
        );
      },
      tdProps: {
        style: {
          verticalAlign: 'baseline'
        }
      }
    },
    {
      title: 'Issue Description',
      key: 'message',
      render: value => {
        const type = value?.dataType;
        const text =
          value?.type === 'TransformationError'
            ? value.message
            : type === 'error'
              ? value.error
              : type === 'mandatory'
                ? value.error
                : value.warning;

        return (
          <Text
            id={`output-table-error-description-${idCreator(type)}-${idCreator(value.tableName)}-${idCreator(value.field)}`}
            fz="14px"
          >
            {text}
          </Text>
        );
      }
    }
  ];

  const allIssues = useMemo(() => {
    if (term) {
      return [..._errors, ..._mandatoryIssues, ..._warnings].filter(
        el =>
          el?.field?.toLowerCase()?.includes(term.toLowerCase()) ||
          el?.tableName?.toLowerCase()?.includes(term.toLowerCase())
      );
    }
    return [..._errors, ..._mandatoryIssues, ..._warnings].filter(el => el);
  }, [_errors, _mandatoryIssues, _warnings, term]);

  const renderLabel = () => {
    if (_errors?.length > 0 && _mandatoryIssues?.length === 0 && _warnings?.length === 0)
      return `Errors Found (${_errors.length})`;

    if (_errors?.length === 0 && _mandatoryIssues?.length > 0 && _warnings?.length === 0)
      return `Mandatory Issues Found (${_mandatoryIssues.length})`;

    if (_errors?.length === 0 && _mandatoryIssues?.length === 0 && _warnings?.length > 0)
      return `Warnings Found (${_warnings.length})`;

    return `Issues Found (${allIssues.length})`;
  };

  const getTrClassName = record => {
    const isMatching =
      issue &&
      issue.table === record.tableName &&
      issue.type === record.dataType &&
      issue.field === record.field;

    const backgroundColor = isMatching
      ? {
          error: 'var(--mantine-color-red-0)',
          mandatory: 'var(--mantine-color-orange-0)'
        }[issue.type] || 'var(--mantine-color-gray-0)'
      : undefined;

    const opacity = issue && !isMatching ? 0.4 : 1;

    return {
      style: {
        backgroundColor,
        opacity,
        cursor: 'pointer'
      }
    };
  };

  const onChangeTerm = value => setTerm(value);

  return (
    <>
      <Box id="output-table-errors-root">
        <Flex
          id="output-table-errors-header"
          fz="16px"
          c="gray.7"
          mb="24px"
          align="center"
          justify="space-between"
        >
          {isOpenSidebar && renderLabel()}

          {hasPanelBtn && (
            <ActionIcon
              id="output-table-errors-toggle-sidebar"
              variant="white"
              onClick={toggleSidebar}
            >
              {isOpenSidebar ? (
                <TbLayoutSidebarRightExpand color="#ADB5BD" fontSize="20px" />
              ) : (
                <TbLayoutSidebarLeftExpand color="#ADB5BD" fontSize="20px" />
              )}
            </ActionIcon>
          )}
        </Flex>

        {isOpenSidebar && (
          <Input
            id="output-table-errors-search"
            placeholder="Search for issues"
            leftSection={<TbSearch fontSize="20px" />}
            size="md"
            mb="24px"
            styles={{
              input: {
                minHeight: '36px',
                height: '36px'
              }
            }}
            onChange={e => onChangeTerm(e.target.value)}
            value={term}
          />
        )}

        {isOpenSidebar ? (
          <Table
            idPrefix="output-table-errors"
            trProps={record => getTrClassName(record)}
            hasIndex={false}
            columns={columns}
            data={allIssues}
            onRowClick={record => handleIssueSelection(record)}
          />
        ) : (
          <Flex id="output-table-errors-collapsed-list" direction="column" gap="8px">
            {allIssues.map((value, index) => {
              const type = value?.dataType;
              const color =
                type === 'error' ? '#E03131' : type === 'mandatory' ? '#FD7E14' : '#868E96';
              const props = getTrClassName(value);
              return (
                <ActionIcon
                  id={`output-table-error-marker-${idCreator(type)}-${idCreator(value.tableName)}-${idCreator(value.field)}`}
                  display="flex"
                  onClick={() => handleIssueSelection(value)}
                  key={`${index} - error`}
                  variant="subtle"
                  color={color}
                  {...props}
                >
                  <TbPoint
                    style={{
                      minWidth: '32px'
                    }}
                    fontSize="32px"
                    color={color}
                  />
                </ActionIcon>
              );
            })}
          </Flex>
        )}
      </Box>
    </>
  );
};
